import { ReactNode } from 'react';
import { lt } from '@mews/framework';
import { Stack, Typography } from '@optimus-web/core';
import { Card, Dash } from '@mews-commander/ui';
import { PaymentPlanDetailResult } from '@mews-commander/data-access';
import { TextKey } from '@mews-commander/enums';
import { PaymentMethodType } from '../../tables/PaymentPlans/PaymentMethodType';
import { PaymentMethodStateTag } from '../../common/PaymentMethodStateTag';
import { PaymentPlanStatusTag } from '../../tables/PaymentPlans/PaymentPlanStatusTag';
import { showNextScheduledPayment } from '../../tables/PaymentPlans/utils';
import { useGetUpcomingPaymentText } from '../../tables/PaymentPlans/hooks';
import { PaymentPlanReservationLink } from '../../views/PaymentPlanDetails/PaymentPlanReservationLink';

const PaymentPlanDetailOverviewItem = ({
    description,
    children,
}: {
    description: Mews.TextKey;
    children: ReactNode;
}) => (
    <Stack vertical spacing="100">
        <Typography textStyle="bodyLarge" variant="secondary" as="div">
            {lt(description)}
        </Typography>
        <Typography textStyle="bodyLarge" as="div">
            {children}
        </Typography>
    </Stack>
);

export const PaymentPlanDetailsCardSection = ({ detail }: { detail: PaymentPlanDetailResult }) => {
    const getUpcomingPaymentText = useGetUpcomingPaymentText();

    const { status, nextPaymentAmount, nextPaymentDateUtc, paymentMethodData } = detail;

    return (
        <Card variant="subtle" outline={false}>
            <Stack distribution="space-between">
                <PaymentPlanDetailOverviewItem description={TextKey.Reservation}>
                    <PaymentPlanReservationLink detail={detail} />
                </PaymentPlanDetailOverviewItem>
                <PaymentPlanDetailOverviewItem description={TextKey.Status}>
                    <PaymentPlanStatusTag status={status} />
                </PaymentPlanDetailOverviewItem>
                <PaymentPlanDetailOverviewItem description={TextKey.PaymentPlanDetailsCardSectionNextPayment}>
                    {showNextScheduledPayment(detail) && nextPaymentAmount && nextPaymentDateUtc ? (
                        getUpcomingPaymentText(nextPaymentAmount, nextPaymentDateUtc)
                    ) : (
                        <Dash />
                    )}
                </PaymentPlanDetailOverviewItem>
                <PaymentPlanDetailOverviewItem description={TextKey.PaymentMethod}>
                    {paymentMethodData ? (
                        <Stack spacing="100" itemAlignment="center">
                            <PaymentMethodType paymentMethodData={paymentMethodData} />
                            <PaymentMethodStateTag
                                paymentMethod={paymentMethodData.discriminator}
                                state={paymentMethodData.value.state}
                            />
                        </Stack>
                    ) : (
                        <Dash />
                    )}
                </PaymentPlanDetailOverviewItem>
            </Stack>
        </Card>
    );
};
